import { createContext, useContext } from "react";
import type {
  AccordionDispatchValue,
  AccordionStateValue,
  AccordionItemContextValue,
} from "./types";

/**
 * Split context pattern to reduce unnecessary re-renders:
 *
 * - DispatchContext: holds toggleItem + type. Stable ref — never triggers re-renders.
 * - StateContext: holds expandedItems Set. Only items that check expansion re-render.
 * - ItemContext: per-item context with pre-computed isExpanded, IDs, disabled state.
 *
 * This means AccordionTrigger/AccordionContent only re-render when their own
 * item's expansion state changes, not when a sibling toggles.
 */

export const AccordionDispatchContext =
  createContext<AccordionDispatchValue | null>(null);

export const AccordionStateContext = createContext<AccordionStateValue | null>(
  null,
);

export const AccordionItemContext =
  createContext<AccordionItemContextValue | null>(null);

export function useAccordionDispatch(): AccordionDispatchValue {
  const ctx = useContext(AccordionDispatchContext);
  if (!ctx) {
    throw new Error(
      "Accordion compound components must be used within <Accordion>",
    );
  }
  return ctx;
}

export function useAccordionState(): AccordionStateValue {
  const ctx = useContext(AccordionStateContext);
  if (!ctx) {
    throw new Error(
      "Accordion compound components must be used within <Accordion>",
    );
  }
  return ctx;
}

export function useAccordionItemContext(): AccordionItemContextValue {
  const ctx = useContext(AccordionItemContext);
  if (!ctx) {
    throw new Error(
      "AccordionTrigger/AccordionContent must be used within <AccordionItem>",
    );
  }
  return ctx;
}
